import { instance } from "../instance";
import { PATH } from "../../shared/constants";
import { getOperatorIdToken } from "../../utils/operatorSessionTokens";

const BASE = `${PATH.API}/${PATH.OPERATOR}/access/admin-chat`;

/**
 * Sent on operator access calls so the server can read Cognito groups
 * (aligned with SessionService.isSuperAdmin).
 * Also required for Downloads / videoboard image APIs.
 */
export function operatorPortalIdTokenHeaders():
| { headers: Record<string, string> }
| Record<string, never> {
  const t = getOperatorIdToken();
  if (!t) {
    console.warn(
      "[operatorPortalIdTokenHeaders] No Cognito IdToken in cookie/storage — log out and back in if Downloads fails.",
    );
    return {};
  }
  return { headers: { "X-Momento-Operator-Id-Token": t } };
}

export type AdminChatThreadSummary = {
  threadKey: string;
  operatorEmail: string;
  operatorDisplayName: string;
  gameId: string | null;
  gameLabel: string;
  lastMessageAt: string;
  lastMessageId?: string;
  lastPreview: string;
  lastFromRole: "operator" | "superAdmin";
  lastFromEmail: string;
  lastFromDisplayName: string;
  unread: boolean;
  closed: boolean;
};

export type AdminChatReactionKind =
  | "thumbsUp"
  | "thumbsDown"
  | "emphasize"
  | "haha"
  | "question";

export type AdminChatMessageRow = {
  _id: string;
  threadKey: string;
  operatorEmail: string;
  gameId: string | null;
  gameLabel: string;
  fromEmail: string;
  /** Profile name from operator access; omitted on older servers. */
  fromDisplayName?: string;
  fromRole: "operator" | "superAdmin";
  body: string;
  attachmentUrls?: string[];
  createdAt: string;
  reactions?: {
    fromEmail: string;
    fromDisplayName: string;
    kind: AdminChatReactionKind;
  }[];
};

export async function getAdminChatThreads(
  viewerEmail: string,
): Promise<AdminChatThreadSummary[]> {
  const { data } = await instance.get<{ threads: AdminChatThreadSummary[] }>(
    `${BASE}/threads`,
    { params: { email: viewerEmail }, ...operatorPortalIdTokenHeaders() },
  );
  return data.threads || [];
}

export async function getAdminChatUnreadCount(viewerEmail: string): Promise<number> {
  const { data } = await instance.get<{ count: number }>(
    `${BASE}/unread-count`,
    { params: { email: viewerEmail }, ...operatorPortalIdTokenHeaders() },
  );
  return typeof data.count === "number" ? data.count : 0;
}

export async function getAdminChatMessages(
  viewerEmail: string,
  threadKey: string,
  limit?: number,
): Promise<AdminChatMessageRow[]> {
  const { data } = await instance.get<{ messages: AdminChatMessageRow[] }>(
    `${BASE}/messages`,
    { params: { email: viewerEmail, threadKey, limit }, ...operatorPortalIdTokenHeaders() },
  );
  return data.messages || [];
}

/**
 * Set or clear a tapback on a message. Same kind again removes (toggle).
 * Pass `null` as kind to clear only.
 */
export async function postAdminChatMessageReaction(
  viewerEmail: string,
  messageId: string,
  kind: AdminChatReactionKind | null,
): Promise<AdminChatMessageRow> {
  const { data } = await instance.post<{ message: AdminChatMessageRow }>(
    `${BASE}/messages/${encodeURIComponent(messageId)}/reaction`,
    { email: viewerEmail, kind },
    operatorPortalIdTokenHeaders(),
  );
  return data.message;
}

export type PresignAdminChatAttachmentResponse = {
  success: boolean;
  uploadKey: string;
  uploadURL: string;
  imageShortUrl: string;
};

export async function presignAdminChatAttachmentUpload(
  viewerEmail: string,
  fileMimeType: string,
): Promise<PresignAdminChatAttachmentResponse> {
  const { data } = await instance.post<PresignAdminChatAttachmentResponse>(
    `${BASE}/attachment-presign`,
    { email: viewerEmail, fileMimeType },
    operatorPortalIdTokenHeaders(),
  );
  return data;
}

export async function postAdminChatMessage(
  viewerEmail: string,
  payload: {
    body: string;
    attachmentUrls?: string[];
    threadKey?: string;
    operatorEmail?: string;
    gameId?: string | null;
    gameLabel?: string;
  },
): Promise<AdminChatMessageRow> {
  const { data } = await instance.post<{ message: AdminChatMessageRow }>(
    `${BASE}/messages`,
    { email: viewerEmail, ...payload },
    operatorPortalIdTokenHeaders(),
  );
  return data.message;
}

export async function markAdminChatRead(
  viewerEmail: string,
  threadKey: string,
): Promise<void> {
  await instance.post(`${BASE}/mark-read`, { email: viewerEmail, threadKey }, operatorPortalIdTokenHeaders());
}

export async function postAdminChatCloseThread(
  viewerEmail: string,
  threadKey: string,
): Promise<void> {
  await instance.post(`${BASE}/close-thread`, { email: viewerEmail, threadKey }, operatorPortalIdTokenHeaders());
}

export async function postAdminChatReopenThread(
  viewerEmail: string,
  threadKey: string,
): Promise<void> {
  await instance.post(`${BASE}/reopen-thread`, { email: viewerEmail, threadKey }, operatorPortalIdTokenHeaders());
}

/**
 * Permanently removes messages and thread record.
 * Thread must be closed first. Super admin only.
 */
export async function postAdminChatDeleteThread(
  viewerEmail: string,
  threadKey: string,
): Promise<void> {
  await instance.post(`${BASE}/delete-thread`, { email: viewerEmail, threadKey }, operatorPortalIdTokenHeaders());
}
